'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { FileText, Briefcase, TrendingUp, Truck } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors';
import * as Sentry from '@sentry/nextjs';
import {
  DASHBOARD_STATS_QUERY,
  type DashboardStats,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  useAdmin,
  useSessionNeedsRenewal,
  SessionRenewalNotice,
} from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';

function StatusTilesSkeleton() {
  return (
    <div className="grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-8">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="bg-white p-3 md:p-6 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200"
        >
          <div className="flex items-start justify-between">
            <div className="flex flex-col min-w-0 md:min-w-fit">
              <Skeleton className="h-3 md:h-4 w-12 md:w-24 mb-1.5 md:mb-3" />
              <Skeleton className="h-6 md:h-8 w-8 md:w-16" />
            </div>
            <Skeleton className="hidden md:block h-12 w-12 rounded-lg" />
          </div>
          <Skeleton className="hidden md:block h-3 w-32 mt-4" />
        </div>
      ))}
    </div>
  );
}

function StatusTilesError() {
  return (
    <div className="grid grid-cols-1 mb-8">
      <div className="bg-red-50 p-6 rounded-xl border border-red-200 text-red-600 flex items-center justify-center">
        <span>Fehler beim Laden der Status-Übersicht.</span>
      </div>
    </div>
  );
}

type StatusTilesErrorKind = 'graphql' | 'server' | 'network' | 'unknown';

/**
 * Diagnose: klassifiziert den Apollo-Fehler, damit in Sentry sichtbar wird,
 * WARUM die Status-Übersicht nicht lädt — Backend-Guard-Meldungen kommen als
 * GraphQL-Error durch (Token abgelaufen / Keycloak 503 / fehlende Kundennummer),
 * Transportfehler tragen einen HTTP-Status. Reines Reporting, kein Verhalten.
 */
function describeStatusTilesError(error: unknown): {
  kind: StatusTilesErrorKind;
  statusCode?: number;
  messages?: string[];
} {
  if (CombinedGraphQLErrors.is(error)) {
    return { kind: 'graphql', messages: error.errors.map((e) => e.message) };
  }
  if (ServerError.is(error) || ServerParseError.is(error)) {
    return { kind: 'server', statusCode: error.statusCode };
  }
  if (error instanceof Error && error.message) {
    return { kind: 'network', messages: [error.message] };
  }
  return { kind: 'unknown' };
}

/**
 * Dashboard-Kacheln: Anzahl UNGELESENER Belege je Belegtyp (Posteingang-Modell).
 * Der Lesestatus wird serverseitig pro User (keycloak_sub) geführt, also
 * geräteübergreifend identisch. Belege gelten als gelesen, sobald sie geöffnet
 * werden (bzw. manuell markiert); Altbestand vor dem Feature-Stichtag zählt nicht.
 */
export function StatusTiles() {
  const { adminQueryVars } = useAdmin();
  // Oberster betroffener Bereich der Seite: hier steht die Aufforderung zum
  // Handeln, weiter unten nur noch der Erklärtext.
  const needsRenewal = useSessionNeedsRenewal();

  const { data, loading, error } = useQuery<{
    dashboardStats: DashboardStats;
  }>(DASHBOARD_STATS_QUERY, {
    variables: { ...adminQueryVars },
    fetchPolicy: 'cache-and-network',
  });

  // Macht das sonst stille "Fehler beim Laden der Status-Übersicht." in Sentry
  // sichtbar — einmal pro auftretendem Fehler. crm.crossLogin / viewedCustomers
  // hängen global bereits an jedem Event (sentry-cross-login-sync). useEffect
  // statt inline → läuft nur im Browser, also SSR-sicher.
  useEffect(() => {
    if (!error) return;
    const info = describeStatusTilesError(error);
    Sentry.captureException(error, {
      tags: {
        'crm.statusTilesError': true,
        'crm.errorKind': info.kind,
        ...(info.statusCode != null ? { 'crm.httpStatus': info.statusCode } : {}),
      },
      extra: { graphqlMessages: info.messages },
      fingerprint: ['crm-status-tiles-load-error', info.kind],
    });
  }, [error]);

  if (loading && !data) {
    return <StatusTilesSkeleton />;
  }

  if (error) {
    // Ist die Session der Grund, hilft die rote Box nicht weiter — der Nutzer
    // braucht die Erklärung und den Weg heraus.
    return needsRenewal ? (
      <SessionRenewalNotice showAction className="mb-8" />
    ) : (
      <StatusTilesError />
    );
  }

  const stats = data?.dashboardStats;

  return (
    <div className="grid grid-cols-4 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-8">
      {/* Offers — ungelesen */}
      <Link
        href="/belege?type=ANGEBOT"
        className="bg-white p-3 md:p-6 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200 hover:border-zinc-300 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group relative overflow-hidden block"
      >
        <div
          className={`absolute top-0 left-0 w-full transition-all ${(stats?.unreadOffers ?? 0) > 0 ? 'bg-status-offer h-1 group-hover:h-1.5' : 'bg-zinc-200 h-1 group-hover:bg-status-offer'}`}
        />
        <div className="md:hidden absolute top-2 right-2 text-zinc-400 group-hover:text-status-offer transition-colors">
          <FileText size={14} strokeWidth={1.8} />
        </div>
        <div className="flex items-start justify-between">
          <div className="flex flex-col min-w-0 md:min-w-fit">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary tracking-tight md:order-2 md:mt-2">
              {stats?.unreadOffers ?? '-'}
            </h3>
            <p className="text-[9px] md:text-sm font-medium text-zinc-500 uppercase tracking-normal md:tracking-wider mt-0.5 md:mt-0 md:order-1 truncate md:whitespace-normal md:overflow-visible">
              Angebote
            </p>
          </div>
          <div className="hidden md:block p-3 bg-zinc-50 text-zinc-600 rounded-lg group-hover:bg-blue-50 group-hover:text-status-offer transition-colors">
            <FileText size={24} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-[9px] md:text-xs text-zinc-500 mt-1 md:mt-4 truncate">ungelesen</p>
      </Link>

      {/* Orders — ungelesen */}
      <Link
        href="/belege?type=AUFTRAG"
        className="bg-white p-3 md:p-6 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200 hover:border-zinc-300 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group relative overflow-hidden block"
      >
        <div
          className={`absolute top-0 left-0 w-full transition-all ${(stats?.unreadOrders ?? 0) > 0 ? 'bg-primary h-1 group-hover:h-1.5' : 'bg-zinc-200 h-1 group-hover:bg-primary'}`}
        />
        <div className="md:hidden absolute top-2 right-2 text-zinc-400 group-hover:text-primary transition-colors">
          <Briefcase size={14} strokeWidth={1.8} />
        </div>
        <div className="flex items-start justify-between">
          <div className="flex flex-col min-w-0 md:min-w-fit">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary tracking-tight md:order-2 md:mt-2">
              {stats?.unreadOrders ?? '-'}
            </h3>
            <p className="text-[9px] md:text-sm font-medium text-zinc-500 uppercase tracking-normal md:tracking-wider mt-0.5 md:mt-0 md:order-1 truncate md:whitespace-normal md:overflow-visible">
              Aufträge
            </p>
          </div>
          <div className="hidden md:block p-3 bg-zinc-50 text-zinc-600 rounded-lg group-hover:bg-orange-50 group-hover:text-primary transition-colors">
            <Briefcase size={24} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-[9px] md:text-xs text-zinc-500 mt-1 md:mt-4 truncate">ungelesen</p>
      </Link>

      {/* Invoices — ungelesen */}
      <Link
        href="/belege?type=RECHNUNG"
        className="bg-white p-3 md:p-6 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200 hover:border-zinc-300 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group relative overflow-hidden block"
      >
        <div
          className={`absolute top-0 left-0 w-full transition-all ${(stats?.unreadInvoices ?? 0) > 0 ? 'bg-status-invoice h-1 group-hover:h-1.5' : 'bg-zinc-200 h-1 group-hover:bg-status-invoice'}`}
        />
        <div className="md:hidden absolute top-2 right-2 text-zinc-400 group-hover:text-status-invoice transition-colors">
          <TrendingUp size={14} strokeWidth={1.8} />
        </div>
        <div className="flex items-start justify-between">
          <div className="flex flex-col min-w-0 md:min-w-fit">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary tracking-tight md:order-2 md:mt-2">
              {stats?.unreadInvoices ?? '-'}
            </h3>
            <p className="text-[9px] md:text-sm font-medium text-zinc-500 uppercase tracking-normal md:tracking-wider mt-0.5 md:mt-0 md:order-1 truncate md:whitespace-normal md:overflow-visible">
              Rechnungen
            </p>
          </div>
          <div className="hidden md:block p-3 bg-zinc-50 text-zinc-600 rounded-lg group-hover:bg-slate-50 group-hover:text-status-invoice transition-colors">
            <TrendingUp size={24} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-[9px] md:text-xs text-zinc-500 mt-1 md:mt-4 truncate">ungelesen</p>
      </Link>

      {/* Delivery Notes — ungelesen */}
      <Link
        href="/belege?type=LIEFERSCHEIN"
        className="bg-white p-3 md:p-6 rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200 hover:border-zinc-300 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group relative overflow-hidden block"
      >
        <div
          className={`absolute top-0 left-0 w-full transition-all ${(stats?.unreadDeliveryNotes ?? 0) > 0 ? 'bg-status-delivery h-1 group-hover:h-1.5' : 'bg-zinc-200 h-1 group-hover:bg-status-delivery'}`}
        />
        <div className="md:hidden absolute top-2 right-2 text-zinc-400 group-hover:text-status-delivery transition-colors">
          <Truck size={14} strokeWidth={1.8} />
        </div>
        <div className="flex items-start justify-between">
          <div className="flex flex-col min-w-0 md:min-w-fit">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary tracking-tight md:order-2 md:mt-2">
              {stats?.unreadDeliveryNotes ?? '-'}
            </h3>
            <p className="text-[9px] md:text-sm font-medium text-zinc-500 uppercase tracking-normal md:tracking-wider mt-0.5 md:mt-0 md:order-1 truncate md:whitespace-normal md:overflow-visible">
              Lieferscheine
            </p>
          </div>
          <div className="hidden md:block p-3 bg-zinc-50 text-zinc-600 rounded-lg group-hover:bg-green-50 group-hover:text-status-delivery transition-colors">
            <Truck size={24} strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-[9px] md:text-xs text-zinc-500 mt-1 md:mt-4 truncate">ungelesen</p>
      </Link>
    </div>
  );
}
